import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Capacitor } from '@capacitor/core';
import { AllRoutesAnalytics, DefaultAnalyticsRoute } from '../../models/Constants';
import { MonitoringService } from '../../services/monitoring/monitoring.service';
import { UserAnalyticsService } from '../user-analytics/user-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class MainAnalyticsService {
  private readonly isBrowser = !Capacitor.isNativePlatform();

  constructor(
    private userAnalyticsS: UserAnalyticsService,
    private monitoringService: MonitoringService,
    private router: Router
  ) { }



  /**
   * Obtiene el nombre formateado de la pantalla actual usando el diccionario AllRoutesAnalytics o fallback de URL limpia.
   */
  public async getScreenName(): Promise<string> {
    try {
      const currentUrl = this.router.url.split('?')[0];
      const filteredPath = Object.keys(AllRoutesAnalytics).filter(path => currentUrl.includes(path));
      if (filteredPath.length > 0) {
        return AllRoutesAnalytics[filteredPath[0] as keyof typeof AllRoutesAnalytics];
      }
      return currentUrl.replace(/^\/|\/$/g, '').replace(/\//g, '_') || DefaultAnalyticsRoute;
    } catch (err) {
      return DefaultAnalyticsRoute;
    }
  }

  /**
   * Envía un evento a Google Tag Manager (GTM).
   * @param eventData Objeto con los datos del evento
   */
  public async sendEventGTM(eventData: Record<string, unknown>): Promise<void> {
    if (this.isBrowser) {
      window['dataLayer'] = window['dataLayer'] || [];
      window['dataLayer'].push({ ...eventData });
    }
  }

  /**
   * Envía un evento nativo a Firebase Analytics.
   * @param eventType Nombre del evento
   * @param eventData Parámetros del evento
   */
  public async logEventFirebase(eventType: string, eventData: Record<string, unknown>): Promise<void> {
    try {
      if (!this.isBrowser) {
        await FirebaseAnalytics.logEvent({
          name: eventType,
          params: eventData
        });
      }
    } catch (error) {
      this.monitoringService.captureException(error)
    }
  }

  /**
   * Establece el ID de usuario en Firebase Analytics.
   * @param userId ID del usuario
   */
  public async setUserId(userId: string): Promise<void> {
    try {
      await FirebaseAnalytics.setUserId({
        userId,
      });
    } catch (error) {
      this.monitoringService.captureException(error);
    }
  }

  /**
   * Establece una propiedad de usuario en Firebase Analytics.
   * @param key Nombre de la propiedad
   * @param value Valor de la propiedad
   */
  public async setUserProperty(key: string, value: string): Promise<void> {
    try {
      await FirebaseAnalytics.setUserProperty({
        key,
        value,
      });
    } catch (error) {
      this.monitoringService.captureException(error);
    }
  }

  /**
   * Sincroniza el ID de usuario y todas sus propiedades globales con Firebase Analytics.
   * Se debe llamar al iniciar la aplicación (si ya hay sesión activa) y tras un inicio de sesión exitoso.
   */
  public async syncUserPropertiesToFirebase(): Promise<void> {
    try {
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      if (!userProps || !userProps.user_id) return;

      // 1. Establecer el User ID
      await this.setUserId(userProps.user_id);

      // 2. Establecer cada una de las propiedades del usuario en Firebase
      for (const [key, value] of Object.entries(userProps)) {
        if (key !== 'user_id' && value !== undefined && value !== null) {
          await this.setUserProperty(key, String(value));
        }
      }
    } catch (error) {
      this.monitoringService.captureException(error);
    }
  }
}

